
import React from "react";
import { useTranslations } from "use-intl";
import { Table, TableBody } from "@/components/ui/table";
import { BankAccountsStatus, Invoice, InvoiceStatus, StatusBadge } from "@/types/payments";
import InvoiceTableHeader from "./table/InvoiceTableHeader";
import InvoiceTableRow from "./table/InvoiceTableRow";
import EmptyTableRow from "./table/EmptyTableRow";
import { useInvoiceTableHelpers } from "@/hooks/useInvoiceTableHelpers";
import { ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";

interface InvoicesTableProps {
  sortedInvoices: Invoice[];
  selectedInvoices: string[];
  toggleSelectInvoice: (invoiceId: string) => void;
  toggleSelectAll: () => void;
  handleSort: (key: keyof Invoice) => void;
  renderSortIcon: (key: keyof Invoice) => React.ReactNode;
  getStatusBadge: (status: InvoiceStatus) => StatusBadge;
  formatCurrency: (amount: number, currency: string) => string;
  filteredInvoices: Invoice[];
  activeTab: string;
  updatePaymentDate?: (invoiceId: string, date: Date) => void;
  onRowClick: (invoice: Invoice) => void;
  onPay?: (invoiceId: string, paymentDate: Date, actualSekAmount?: number) => void;
  onCancel?: (invoiceId: string) => void;
  onExport?: (invoiceId: string) => void;
  onDownload?: (invoiceId: string, fileName: string) => void;
  onEditPayment?: (invoiceId: string) => void;
  onEditVendor?: (invoiceId: string) => void;
  onEditInvoice?: (invoiceId: string) => void;
  onConfirmMatch?: (payableId: string, transactionId: string) => Promise<boolean>;
  sortConfig?: { key: keyof Invoice | null; direction: 'asc' | 'desc' | 'none' };
  /** Pass-through — see BankAccountsStatus's docstring (#498 whole-branch review finding I2). */
  hasBankAccounts?: BankAccountsStatus;
}

const InvoicesTable = ({
  sortedInvoices,
  selectedInvoices,
  toggleSelectInvoice,
  toggleSelectAll,
  handleSort,
  renderSortIcon,
  getStatusBadge,
  formatCurrency,
  filteredInvoices,
  activeTab,
  updatePaymentDate,
  onRowClick,
  onPay,
  onCancel,
  onExport,
  onDownload,
  onEditPayment,
  onEditVendor,
  onEditInvoice,
  onConfirmMatch,
  sortConfig,
  hasBankAccounts
}: InvoicesTableProps) => {
  const t = useTranslations("consultantPayables.table");
  // Always show checkboxes now
  const showCheckboxes = true;
  
  // Payment date is always shown now
  const showPaymentDate = true;

  const { 
    disablePastDates, 
    getPaymentDateObject, 
    isLatePayment,
    isHolidayAdjusted 
  } = useInvoiceTableHelpers();

  return (
    <div className="rounded-md border">
      <Table>
        <InvoiceTableHeader 
          showCheckboxes={showCheckboxes}
          toggleSelectAll={toggleSelectAll}
          handleSort={handleSort}
          renderSortIcon={renderSortIcon}
          filteredInvoices={filteredInvoices}
          selectedInvoices={selectedInvoices}
          sortConfig={sortConfig}
        />
        <TableBody>
          {sortedInvoices.length > 0 ? (
            sortedInvoices.map(invoice => (
              <InvoiceTableRow
                key={invoice.id}
                invoice={invoice}
                showCheckboxes={showCheckboxes}
                toggleSelectInvoice={toggleSelectInvoice}
                selectedInvoices={selectedInvoices}
                showPaymentDate={showPaymentDate}
                updatePaymentDate={updatePaymentDate}
                getStatusBadge={getStatusBadge}
                formatCurrency={formatCurrency}
                onRowClick={onRowClick}
                disablePastDates={disablePastDates}
                getPaymentDateObject={getPaymentDateObject}
                isLatePayment={isLatePayment}
                isHolidayAdjusted={isHolidayAdjusted}
                onPay={onPay}
                onCancel={onCancel}
                onExport={onExport}
                onDownload={onDownload}
                onEditPayment={onEditPayment}
                onConfirmMatch={onConfirmMatch}
                hasBankAccounts={hasBankAccounts}
              />
            ))
          ) : (
            <EmptyTableRow
              colSpan={showCheckboxes ? 10 : 9}
              message={t(filteredInvoices.length === 0 ? "noMatch" : "none")}
            />
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default InvoicesTable;
